import { useCallback, useState } from 'react';
import { ImportModalView, UploadErrorType } from '../types';
const UPLOADING_MIN_DURATION_MS = 1000;
export function useSpreadsheetImport() {
    const [currentView, setCurrentView] = useState(ImportModalView.SELECTION);
    const [file, setFile] = useState(null);
    const [validatedRows, setValidatedRows] = useState([]);
    const [uploadErrorType, setUploadErrorType] = useState(null);
    const [inlineError, setInlineError] = useState(null);
    const [uploadDate, setUploadDate] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);
    const [validationErrorSheet, setValidationErrorSheet] = useState(null);
    const handleFileSelected = useCallback((selectedFile) => {
        if (!selectedFile) {
            setFile(null);
            return;
        }
        setFile(selectedFile);
        setInlineError(null);
        setCurrentView(ImportModalView.UPLOADING);
        setTimeout(() => {
            setUploadDate(new Date());
            setCurrentView(ImportModalView.FILE_LOADED);
        }, UPLOADING_MIN_DURATION_MS);
    }, []);
    const applyOutcome = useCallback((outcome) => {
        if (outcome.kind === 'SUCCESS') {
            setValidatedRows(outcome.validatedRows);
            setValidationErrors([]);
            setValidationErrorSheet(null);
            setCurrentView(ImportModalView.SUCCESS);
            return;
        }
        if (outcome.kind === 'VALIDATION_FAILED') {
            setValidationErrors(outcome.errors);
            setValidationErrorSheet(outcome.errorSheet ?? null);
            setCurrentView(ImportModalView.VALIDATION_RESULT);
            return;
        }
        if (outcome.kind === 'FILE_ERROR') {
            if (outcome.inline) {
                setFile(null);
                setValidatedRows([]);
                setUploadDate(null);
                setValidationErrors([]);
                setValidationErrorSheet(null);
                setUploadErrorType(null);
                setInlineError(outcome.uploadErrorType);
                setCurrentView(ImportModalView.UPLOAD);
                return;
            }
            setUploadErrorType(outcome.uploadErrorType);
            setCurrentView(ImportModalView.UPLOAD_ERROR);
            return;
        }
        setUploadErrorType(UploadErrorType.SYSTEMIC);
        setCurrentView(ImportModalView.UPLOAD_ERROR);
    }, []);
    const handleImport = useCallback(async (runImport) => {
        setCurrentView(ImportModalView.LOADING);
        try {
            const outcome = await runImport();
            applyOutcome(outcome);
        }
        catch {
            applyOutcome({ kind: 'SYSTEMIC_ERROR' });
        }
    }, [applyOutcome]);
    const resetState = useCallback(() => {
        setCurrentView(ImportModalView.SELECTION);
        setFile(null);
        setValidatedRows([]);
        setUploadErrorType(null);
        setInlineError(null);
        setUploadDate(null);
        setValidationErrors([]);
        setValidationErrorSheet(null);
    }, []);
    const handleResubmit = useCallback(() => {
        setFile(null);
        setValidatedRows([]);
        setUploadErrorType(null);
        setInlineError(null);
        setUploadDate(null);
        setValidationErrors([]);
        setValidationErrorSheet(null);
        setCurrentView(ImportModalView.UPLOAD);
    }, []);
    return {
        currentView,
        file,
        validatedRows,
        uploadErrorType,
        inlineError,
        uploadDate,
        validationErrors,
        validationErrorSheet,
        setCurrentView,
        setUploadErrorType,
        handleFileSelected,
        handleImport,
        resetState,
        handleResubmit,
        setInlineError,
    };
}
